import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import SafeIcon from '../../common/SafeIcon';
import { formatCurrency, formatDate } from '../../utils/helpers';
import { useCRM } from '../../context/CRMContext';
import { useCRMData } from '../../context/CRMDataContext';
import { useTeam } from '../../context/TeamContext';
import ActivityForm from '../forms/ActivityForm';
import supabase from '../../supabase/supabase';

const TAG_LIBRARY_KEY = 'saleswork_demo_tag_library';
const DEMO_CUSTOMERS_KEY = 'saleswork_demo_customers';
const DEMO_SALES_EVENTS_KEY = 'saleswork_demo_sales_events';
const TAG_COLOR_CLASSES = {
  blue: 'bg-blue-50 text-blue-700 border-blue-200',
  green: 'bg-green-50 text-green-700 border-green-200',
  purple: 'bg-purple-50 text-purple-700 border-purple-200',
  orange: 'bg-orange-50 text-orange-700 border-orange-200',
  red: 'bg-red-50 text-red-700 border-red-200',
  gray: 'bg-gray-100 text-gray-700 border-gray-200'
};

const STATUS_OPTIONS = ['Ny', 'Kontaktet', 'Kvalifisert', 'Møte', 'Tilbud', 'Vunnet', 'Tapt'];

const getDisplayStatus = status => {
  const statusMap = {
    customer: 'Kunde',
    closed_won: 'Vunnet',
    won: 'Vunnet',
    lost: 'Tapt',
    open: 'Åpen',
    'Åpen': 'Åpen',
    'Vunnet': 'Vunnet',
    'Tapt': 'Tapt'
  };

  return statusMap[status] || status || 'Ikke satt';
};

const normalizeTags = value => {
  if (!value) return [];

  if (Array.isArray(value)) {
    return value
      .map(tag => {
        if (!tag) return '';
        if (typeof tag === 'string') return tag;
        if (typeof tag === 'object') {
          return tag.name || tag.label || tag.title || tag.value || '';
        }
        return String(tag);
      })
      .map(tag => tag.trim())
      .filter(Boolean);
  }

  if (typeof value === 'string') {
    return value.split(',').map(tag => tag.trim()).filter(Boolean);
  }

  return [];
};

const getTagLibrary = () => {
  try {
    const raw = localStorage.getItem(TAG_LIBRARY_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const saveTagLibrary = library => {
  try {
    localStorage.setItem(TAG_LIBRARY_KEY, JSON.stringify(library));
  } catch {}
};

const getOrCreateTagMeta = tagName => {
  const name = String(tagName || '').trim();
  if (!name) return null;

  const library = getTagLibrary();
  const existing = library.find(tag => tag?.name?.toLowerCase() === name.toLowerCase());
  if (existing) return existing;

  const next = {
    name,
    color: ['blue', 'green', 'purple', 'orange', 'red', 'gray'][library.length % 6]
  };

  saveTagLibrary([...library, next]);
  return next;
};

const getTagMeta = tagName => {
  const name = String(tagName || '').trim();
  const existing = getTagLibrary().find(tag => tag?.name?.toLowerCase() === name.toLowerCase());
  return existing || { name, color: 'gray' };
};

const getTagClassName = color => TAG_COLOR_CLASSES[color] || TAG_COLOR_CLASSES.gray;

const getDomainFromUrl = url => {
  try {
    if (!url) return '';
    const normalized = url.startsWith('http') ? url : `https://${url}`;
    return new URL(normalized).hostname.replace(/^www\./, '');
  } catch {
    return '';
  }
};

const getLogoUrlFromWebsite = website => {
  const domain = getDomainFromUrl(website);
  return domain ? `https://www.google.com/s2/favicons?domain=${domain}&sz=128` : '';
};

const getWebsiteHref = website => {
  if (!website) return '';
  return website.startsWith('http') ? website : `https://${website}`;
};

const readDemoList = key => {
  try {
    const raw = localStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeDemoList = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {}
};

const dedupeTimelineItems = (items, fallbackDealId) => {
  const seen = new Set();

  return items.filter(item => {
    const type = item.type || item.activityType || item.title;
    const dealId = item.dealId || item.deal_id || fallbackDealId;
    const key = type === 'customer_conversion'
      ? `${dealId || 'deal'}-customer_conversion`
      : item.id || `${dealId}-${type}-${item.createdAt}`;

    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const getLogMessage = (log, ownerName) => {
  if (log.type === 'customer_conversion') {
    return `${log.user || ownerName} konverterte denne leaden`;
  }

  if (log.type === 'Notat') return 'La til notat';

  const activityTypes = new Set(['Oppgave', 'Samtale', 'E-post', 'Møte', 'Aktivitet']);
  const actionValue = String(
    log.action || log.event || log.eventType || log.actionType || log.operation || ''
  ).toLowerCase();
  const statusValue = String(log.status || '').toLowerCase();
  const isActivity = activityTypes.has(log.type) ||
    Boolean(log.activityType || log.activity_id || log.activityId);

  if (isActivity) {
    if (['gjennomført', 'fullført', 'completed', 'complete'].includes(statusValue)) {
      return 'Fullførte aktivitet';
    }

    if (/(update|updated|oppdater)/.test(actionValue)) {
      return 'Oppdaterte aktivitet';
    }

    return 'Opprettet aktivitet';
  }

  return 'Oppdaterte lead';
};

const getOwnerId = deal => (
  deal?.owner_user_id ||
  deal?.user_id ||
  deal?.assigned_user_id ||
  deal?.ownerId ||
  null
);

const getUserDisplayName = user => user?.name || user?.email || 'Ukjent bruker';

const DealDrawer = ({
  deal,
  company,
  contact,
  isOpen,
  onClose,
  onEdit,
  isActivityModalOpen = false,
  onUpdate
}) => {
  const navigate = useNavigate();
  const {
    currentUser,
    updateDeal
  } = useCRM();
  const {
    activities,
    addActivity,
    updateActivity
  } = useCRMData();
  const { users: teamUsers = [] } = useTeam();

  const [isAddingNote, setIsAddingNote] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [isOwnerSelectOpen, setIsOwnerSelectOpen] = useState(false);
  const [isFullView, setIsFullView] = useState(false);
  const [status, setStatus] = useState(deal?.status || 'Ny');
  const [tags, setTags] = useState(normalizeTags(deal?.tags || deal?.labels || deal?.tagList));
  const [localDeal, setLocalDeal] = useState(deal);
  const [isAddingTag, setIsAddingTag] = useState(false);
  const [newTag, setNewTag] = useState('');
  const [logoFailed, setLogoFailed] = useState(false);
  const [showInlineActivityForm, setShowInlineActivityForm] = useState(false);
  const [conversionMessage, setConversionMessage] = useState('');

  useEffect(() => {
    setLocalDeal(deal);
    setTags(normalizeTags(deal?.tags || deal?.labels || deal?.tagList));
    setStatus(deal?.status || 'Ny');
    setLogoFailed(false);
    setConversionMessage('');
  }, [deal]);

  useEffect(() => {
    if (isOpen) setIsFullView(false);
  }, [isOpen, deal?.id, deal?.deal_id]);

  if (!isOpen || !deal) return null;

  const currentDeal = localDeal || deal;
  const ownerId = getOwnerId(currentDeal);
  const currentUserId = currentUser?.user_id || currentUser?.id || currentUser?.auth_user_id;
  const owner = ownerId
    ? teamUsers.find(user => user?.id === ownerId || user?.user_id === ownerId)
    : null;
  const displayOwnerName = ownerId
    ? getUserDisplayName(owner)
    : 'Ikke tildelt';
  const displayAddress = company?.visiting_address || company?.address || company?.postal_address || '';
  const naceValue = [company?.nace_code, company?.nace_description]
    .filter(value => value !== undefined && value !== null && value !== '')
    .join(' – ');
  const brregFields = [
    ['Org.nr.', company?.org_number],
    ['Selskapsform', company?.company_type],
    ['Bransje', company?.industry],
    ['NACE', naceValue],
    ['Ansatte', company?.employees_count],
    ['BRREG-status', company?.brreg_status]
  ].filter(([, value]) => value !== undefined && value !== null && value !== '');

  const normalizedTags = normalizeTags(
    currentDeal?.tags || currentDeal?.labels || currentDeal?.tagList || tags
  );
  const contextActivities = (activities || [])
    .filter(a => (a.deal_id || a.dealId) === (currentDeal.deal_id || currentDeal.id))
    .map(activity => ({
      ...activity,
      logId: activity.activity_id || activity.id,
      logDate: activity.date || activity.created_at
    }));
  const dealLogs = Array.isArray(currentDeal.logs)
    ? currentDeal.logs.map(log => ({
        ...log,
        logId: log.id || `deal-log-${log.createdAt || log.created_at || log.title}`,
        logDate: log.createdAt || log.created_at || log.date
      }))
    : [];
  const dealActivitiesSource = contextActivities.length > 0 ? contextActivities : dealLogs;
  const dealActivities = dealActivitiesSource
    .filter((log, index, allLogs) => allLogs.findIndex(item =>
      item.logId && log.logId
        ? item.logId === log.logId
        : item.type === log.type && item.title === log.title && item.logDate === log.logDate
    ) === index)
    .sort((a, b) => new Date(b.logDate || 0) - new Date(a.logDate || 0));
  const visibleDealActivities = dedupeTimelineItems(
    dealActivities,
    currentDeal.deal_id || currentDeal.id
  );

  const displayContactName = currentDeal.contactName || currentDeal.name || contact?.name || '';
  const displayCompanyName = currentDeal.companyName || currentDeal.company || company?.name || '';
  const displayTitle = currentDeal.jobTitle || currentDeal.title || contact?.title || '';
  const displayPhone = contact?.phone || deal.phone || '';
  const displayEmail = contact?.email || company?.email || deal.email || '';
  const displayWebsite = company?.website || deal.website || '';
  const websiteHref = getWebsiteHref(displayWebsite);
  const displayWebsiteLabel = getDomainFromUrl(displayWebsite) || displayWebsite;
  const generatedLogoUrl = getLogoUrlFromWebsite(displayWebsite);
  const displayLogoUrl = currentDeal.logoUrl || currentDeal.companyLogo || company?.logoUrl ||
    company?.companyLogo || company?.logo || (displayWebsite ? generatedLogoUrl : '');
  const isGeneratedLogo = Boolean(displayLogoUrl && generatedLogoUrl && displayLogoUrl === generatedLogoUrl);
  const displaySource = deal.source || '';
  const displayCountry = company?.country || deal.country || '';
  const hasProbability = deal.probability !== undefined && deal.probability !== null && deal.probability !== '';
  const displayProbability = hasProbability && !(status === 'Ny' && Number(deal.probability) === 10)
    ? `${deal.probability}%`
    : 'Ikke vurdert';
  const isDealCustomer = currentDeal?.isCustomer === true ||
    currentDeal?.convertedToCustomer === true ||
    currentDeal?.customerStatus === 'active' ||
    currentDeal?.status === 'customer' ||
    currentDeal?.status === 'closed_won' ||
    currentDeal?.status === 'Vunnet';
  const conversionDate = currentDeal.convertedAt || currentDeal.closedWonAt || currentDeal.wonAt;
  const conversionLogExists = Array.isArray(currentDeal.logs) &&
    currentDeal.logs.some(log => log.type === 'customer_conversion');

  const handleFullView = () => {
    const currentDealId = deal.deal_id || deal.id;

    if (deal.company_id && currentDealId) {
      onClose?.();
      navigate(`/companies/${deal.company_id}?tab=Tidslinje&deal_id=${currentDealId}`);
      return;
    }

    setIsFullView(true);
  };

  const handleClaimLead = async () => {
    if (!currentUserId) return;

    const currentDealId = currentDeal.id || currentDeal.deal_id;
    if (!currentDealId) return;

    const { error } = await supabase
      .from('deals_1710925200000')
      .update({ user_id: currentUserId })
      .eq('id', currentDealId);

    if (error) {
      console.error('Failed to claim deal:', error);
      return;
    }

    setLocalDeal(previousDeal => ({
      ...(previousDeal || currentDeal),
      user_id: currentUserId,
      owner_user_id: currentUserId
    }));
    setIsOwnerSelectOpen(false);
  };

  const handleOwnerChange = async selectedOwner => {
    const selectedOwnerId = selectedOwner
      ? selectedOwner.user_id || selectedOwner.id || null
      : null;
    const currentDealId = currentDeal.id || currentDeal.deal_id;

    if (!currentDealId) return;

    const { error } = await supabase
      .from('deals_1710925200000')
      .update({ user_id: selectedOwnerId })
      .eq('id', currentDealId);

    if (error) {
      console.error('Failed to update deal owner:', error);
      return;
    }

    setLocalDeal(previousDeal => ({
      ...(previousDeal || currentDeal),
      user_id: selectedOwnerId,
      owner_user_id: selectedOwnerId
    }));
    setIsOwnerSelectOpen(false);
  };

  const handleStatusChange = async event => {
    const newStatus = event.target.value;
    setStatus(newStatus);
    await updateDeal(deal.deal_id, {
      status: newStatus,
      updatedAt: new Date().toISOString()
    });
  };

  const handleSaveNote = () => {
    if (!newNote.trim()) return;

    addActivity({
      type: 'Notat',
      title: 'Notat lagt til',
      description: newNote,
      deal_id: deal.deal_id,
      company_id: deal.company_id,
      contact_id: deal.contact_id,
      status: 'Gjennomført',
      date: new Date().toISOString(),
      user_id: currentUser?.user_id
    });

    setNewNote('');
    setIsAddingNote(false);
  };

  const getLogIcon = type => {
    switch (type) {
      case 'Notat': return '📝';
      case 'Samtale': return '📞';
      case 'E-post': return '📧';
      case 'Møte': return '👥';
      case 'Oppgave': return '✅';
      case 'Oppdatert': return '👥';
      case 'customer_conversion': return '🏆';
      default: return '📅';
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: isActivityModalOpen ? 0 : 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            aria-hidden="true"
            className={`fixed inset-0 bg-slate-900/30 backdrop-blur-sm z-[60] ${
              isActivityModalOpen ? 'pointer-events-none' : ''
            }`}
          />
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className={isFullView
              ? 'fixed inset-0 w-full h-full bg-white shadow-2xl z-[70] flex flex-col md:flex-row'
              : 'fixed inset-y-0 right-0 w-full md:w-[1000px] bg-white shadow-2xl z-[70] flex flex-col md:flex-row'}
          >
            <div className="w-full md:w-[350px] bg-[#f8fafc] border-r border-slate-200 flex flex-col h-full overflow-hidden">
              <div className="p-6 pb-4">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-slate-200 rounded-full flex items-center justify-center text-xl font-bold text-slate-600">
                    {displayContactName ? displayContactName.substring(0, 2).toUpperCase() : 'E'}
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-slate-900 leading-tight">{displayContactName || deal.title}</h2>
                    <div className="flex items-center gap-2 mt-1">
                      <select value={status} onChange={handleStatusChange} className="px-2 py-1 bg-slate-200 text-slate-700 text-[10px] font-bold rounded-md border-0 outline-none">
                        {STATUS_OPTIONS.map(option => <option key={option} value={option}>{getDisplayStatus(option)}</option>)}
                      </select>
                      {isDealCustomer && <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-1 text-[10px] font-black uppercase tracking-wide text-emerald-700">Kunde</span>}
                    </div>
                    {isDealCustomer && conversionDate && <p className="mt-1 text-[10px] font-medium text-emerald-700">Konvertert {formatDate(conversionDate)}</p>}
                  </div>
                </div>

                <div className="flex items-center gap-2 text-sm text-slate-600 font-medium">
                  <SafeIcon icon="🏢" className="w-4 h-4 text-slate-400" />
                  {displayCompanyName || 'Ukjent Selskap'}
                </div>

                <div className="flex items-center gap-3 mt-4">
                  <div className="w-10 h-10 rounded-lg bg-white border border-slate-200 flex items-center justify-center overflow-hidden">
                    {displayLogoUrl && !logoFailed ? (
                      <img src={displayLogoUrl} alt="" className="w-7 h-7 object-contain" onError={() => setLogoFailed(true)} />
                    ) : <SafeIcon icon="🏢" className="w-5 h-5 text-slate-400" />}
                  </div>
                  <span className="text-xs text-slate-500">{isGeneratedLogo ? 'Firmasymbol' : 'Firmalogo'}</span>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-6 pt-2 space-y-4">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="font-bold text-slate-900 text-sm">Lead Information</h3>
                  <button onClick={() => onEdit(deal)} className="text-blue-600 hover:text-blue-800"><SafeIcon icon="✏️" className="w-4 h-4" /></button>
                </div>
                <div className="space-y-3 text-sm">
                  <div><span className="text-slate-500 w-24 inline-block font-medium">Eier:</span>{displayOwnerName}</div>
                  <div><span className="text-slate-500 w-24 inline-block font-medium">Title:</span>{displayTitle || 'Ikke registrert'}</div>
                  <div><span className="text-slate-500 w-24 inline-block font-medium">Mobile:</span>{displayPhone || 'Ikke registrert'}</div>
                  <div><span className="text-slate-500 w-24 inline-block font-medium">E-post:</span>{displayEmail || 'Ikke registrert'}</div>
                  <div><span className="text-slate-500 w-24 inline-block font-medium">Website:</span>{displayWebsite || 'Ikke registrert'}</div>
                  <div><span className="text-slate-500 w-24 inline-block font-medium">Source:</span>{displaySource}</div>
                  <div><span className="text-slate-500 w-24 inline-block font-medium">Deal size:</span>{formatCurrency(deal.value)}</div>
                  <div><span className="text-slate-500 w-24 inline-block font-medium">Probability:</span>{displayProbability}</div>
                  <div><span className="text-slate-500 w-24 inline-block font-medium">Address:</span>{displayAddress || 'Ikke registrert'}</div>
                  <div><span className="text-slate-500 w-24 inline-block font-medium">City:</span>{company?.city || 'Ikke registrert'}</div>
                  <div><span className="text-slate-500 w-24 inline-block font-medium">Country:</span>{displayCountry || 'Ikke registrert'}</div>
                </div>

                {brregFields.length > 0 && (
                  <div className="border-t border-slate-200 pt-4 space-y-3 text-sm">
                    {brregFields.map(([label, value]) => (
                      <div key={label}><span className="text-slate-500 w-24 inline-block font-medium">{label}:</span>{value}</div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="flex-1 flex flex-col bg-white h-full overflow-hidden relative">
              <div className="h-[72px] border-b border-slate-200 px-6 flex items-center justify-end gap-3 bg-white">
                <div className="relative">
                  <button onClick={() => setIsOwnerSelectOpen(!isOwnerSelectOpen)} className="flex items-center gap-2 p-1.5 pr-3 hover:bg-slate-50 rounded-lg border border-transparent hover:border-slate-200 transition-colors">
                    {owner?.avatar ? <img src={owner.avatar} className="w-8 h-8 rounded-full border border-slate-200" alt="Owner" /> : <div className="w-8 h-8 rounded-full bg-slate-100 border border-slate-200" />}
                    <div className="text-left"><p className="text-sm font-bold text-slate-900 leading-none">{displayOwnerName}</p><p className="text-[10px] text-slate-500 uppercase tracking-widest mt-0.5">Lead Owner</p></div>
                    <SafeIcon icon="▼" className="w-3 h-3 text-slate-400 ml-1" />
                  </button>
                  {isOwnerSelectOpen && (
                    <div className="absolute right-0 top-full mt-2 z-20 min-w-[220px] rounded-lg border border-slate-200 bg-white p-1 shadow-xl">
                      <button
                        type="button"
                        onClick={() => handleOwnerChange(null)}
                        className="w-full rounded-md px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
                      >
                        Ikke tildelt
                      </button>
                      {teamUsers.map(user => {
                        const userId = user?.user_id || user?.id;
                        if (!userId) return null;

                        return (
                          <button
                            key={userId}
                            type="button"
                            onClick={() => handleOwnerChange(user)}
                            className="w-full rounded-md px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
                          >
                            {getUserDisplayName(user)}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
                {!ownerId && (
                  <button
                    type="button"
                    onClick={handleClaimLead}
                    disabled={!currentUserId}
                    title={!currentUserId ? 'Kan ikke plukke lead – bruker mangler ID' : 'Plukk lead'}
                    className="px-4 py-2 bg-blue-600 font-bold text-sm text-white rounded-md hover:bg-blue-700 transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Plukk lead
                  </button>
                )}
                <button onClick={handleFullView} className="px-4 py-2 bg-slate-100 font-bold text-sm text-slate-700 rounded-md hover:bg-slate-200 transition-colors">Full view</button>
                <button onClick={() => onEdit(deal)} className="px-4 py-2 bg-slate-100 font-bold text-sm text-slate-700 rounded-md hover:bg-slate-200 transition-colors">Update</button>
                <button onClick={onClose} className="p-2 text-slate-400"><SafeIcon icon="✕" className="w-5 h-5" /></button>
              </div>

              <div className="px-6 py-4 border-b border-slate-200 bg-white flex flex-wrap gap-2 relative">
                <button onClick={() => setIsAddingNote(!isAddingNote)} className="flex items-center gap-2 px-4 py-2 bg-[#1a73e8] text-white rounded-md text-sm font-bold hover:bg-blue-700 transition-colors"><SafeIcon icon="📝" className="w-4 h-4" /> Add Note</button>
                <button type="button" onClick={() => setShowInlineActivityForm(true)} className="flex items-center gap-2 px-4 py-2 bg-[#1a73e8] text-white rounded-md text-sm font-bold hover:bg-blue-700 transition-colors"><SafeIcon icon="📅" className="w-4 h-4" /> New Activity</button>
              </div>

              <div className="flex-1 overflow-y-auto p-6 bg-white relative">
                <h3 className="text-base font-bold text-slate-900 mb-6">Logs</h3>
                {showInlineActivityForm && <div className="mb-6 border border-slate-200 rounded-xl bg-slate-50 overflow-hidden"><ActivityForm activity={{ deal_id: deal.deal_id || deal.id, company_id: deal.company_id, contact_id: deal.contact_id }} onClose={() => setShowInlineActivityForm(false)} /></div>}
                {isAddingNote && <div className="mb-6 border border-slate-300 rounded-lg p-4 bg-slate-50"><textarea autoFocus value={newNote} onChange={e => setNewNote(e.target.value)} placeholder="Type a note..." className="w-full bg-white border border-slate-200 rounded p-3 text-sm min-h-[80px] resize-none" /><button onClick={handleSaveNote} className="mt-3 px-4 py-1.5 bg-[#1a73e8] text-white text-sm font-bold rounded">Save Note</button></div>}
                {visibleDealActivities.length ? visibleDealActivities.map((log, index) => (
                  <div key={log.logId || index} className="border border-slate-200 rounded-lg p-4 mb-4">
                    <h4 className="text-sm font-bold text-slate-900">{log.title}</h4>
                    <p className="text-sm text-slate-700 mt-1">{getLogMessage(log, displayOwnerName)}</p>
                    {log.description && <p className="mt-2 text-sm text-slate-600">{log.description}</p>}
                  </div>
                )) : <p className="text-sm text-slate-500">Ingen aktiviteter registrert.</p>}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default DealDrawer;